/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type { HostContext } from '../@types';
import platform from 'platform';
import bowser from 'bowser';
import { getErrorMessage } from './getErrorMessage';
import { getLogger } from './SearchLogger';
import { langScript } from '@intl/adobe-locales';

export class Utils {
	// Singleton
	static instance;
	readonly searchHostContext: HostContext;

	constructor(searchHostContext: HostContext) {
		if (!Utils.instance) {
			this.searchHostContext = searchHostContext;
			Utils.instance = this;
		}

		return Utils.instance;
	}

	static dropDownShowing = false;

	// AAM PLugin related helper functions
	// get the AAM plugin installed on the current browser
	getAAMPlugin() {
		let aamPlugin: Plugin | undefined;

		if (navigator?.plugins) {
			aamPlugin = Object.values(navigator.plugins).find((plugin) =>
				plugin?.name?.toLowerCase().includes('adobeaamdetect'),
			);
		}

		if (!aamPlugin && platform.name === 'IE') {
			try {
				// @ts-ignore
				aamPlugin = new ActiveXObject('AdobeAAMDetect.AdobeAAMDetect');
			} catch (e) {
				getLogger().info('Globals', 'AdobeAAMDetect ActiveXObject unavailable', e);
				return null;
			}
		}

		return aamPlugin;
	}

	/* Currently, there appears to be no foolproof way to detect this, but we'll get as close as we can */
	is64bitWindows() {
		if (platform?.os?.architecture) {
			return platform.os.architecture === 64 && this.isWin();
		}
	}

	isWin(): boolean {
		if (platform?.os?.family) {
			return platform.os.family.toLowerCase().includes('windows');
		}
	}

	isMacOS(): boolean {
		if (platform?.os?.family) {
			return platform.os.family.toLowerCase().includes('os x');
		}

		return false;
	}

	isLinux(): boolean {
		if (platform?.os?.family) {
			return platform.os.family.toLowerCase() === 'linux' || platform.os.family.toLowerCase() === 'ubuntu';
		}
		return false;
	}

	getPlatform(): string {
		const clientPlatform = {
			mac: /os x|mac/i,
			win: /win/i,
		};
		const userAgent = navigator.userAgent.toLowerCase();

		// if ccd appContext available use that first
		if (this.searchHostContext.hostInfo.surface === 'CCDesktop' && globalThis.appContext) {
			return globalThis.appContext.platform;
		}
		for (const platformName in clientPlatform) {
			if (clientPlatform[platformName].test(userAgent)) {
				return platformName;
			}
		}

		return 'all';
	}

	is64bitMac() {
		if (platform?.os?.architecture) {
			return platform.os.architecture === 64 && this.isMacOS();
		}
	}

	isChrome() {
		if (platform?.name) {
			return platform.name.toLowerCase().includes('chrome');
		}

		return false;
	}

	static isSafari() {
		if (platform?.name) {
			return platform.name.toLowerCase().includes('safari');
		}

		return false;
	}

	static isEdge() {
		if (platform?.name) {
			return platform.name.toLowerCase().includes('edge');
		}

		return false;
	}

	static isIE() {
		if (platform?.name) {
			return platform.name.toLowerCase().includes('ie');
		}

		return false;
	}

	static isIOS() {
		let check = false;
		const input = navigator.userAgent || navigator.vendor || globalThis.opera;

		// https://www.neowin.net/news/ie11-fakes-user-agent-to-fool-gmail-in-windows-phone-81-gdr1-update
		// We need to add the check for window.MSStream because IE-11 fakes it's user-agent.
		if (/iPad|iPhone|iPod/.test(input) && !globalThis.MSStream) {
			check = true;
		}

		return check;
	}

	/**
	 * Detects if the current browser is a mobile or tablet - check the useragent to match for ios, android and windows phone
	 */
	static isMobileOrTabletBrowser() {
		let check = false;
		const input = navigator.userAgent || navigator.vendor || globalThis.opera;

		// https://www.neowin.net/news/ie11-fakes-user-agent-to-fool-gmail-in-windows-phone-81-gdr1-update
		// We need to add the check for window.MSStream because IE-11 fakes it's user-agent.
		if (
			/windows phone/i.test(input) ||
			/android/i.test(input) ||
			(/iPad|iPhone|iPod/.test(input) && !globalThis.MSStream)
		) {
			check = true;
		}

		return check;
	}

	// Checking browsers that cannot run CCHome
	isUnsupportedBrowser() {
		return (
			bowser.isUnsupportedBrowser(
				{
					chrome: '24',
				},
				globalThis.navigator.userAgent,
			) ||
			bowser.isUnsupportedBrowser(
				{
					safari: '8',
				},
				globalThis.navigator.userAgent,
			) ||
			bowser.isUnsupportedBrowser(
				{
					firefox: '29',
				},
				globalThis.navigator.userAgent,
			) ||
			bowser.isUnsupportedBrowser(
				{
					msedge: '12',
				},
				globalThis.navigator.userAgent,
			) ||
			bowser.isUnsupportedBrowser(
				{
					msie: '11',
				},
				globalThis.navigator.userAgent,
			)
		);
	}

	// Checking browsers that can run CCHome but users should update
	isDeprecatedBrowser() {
		return (
			!this.isUnsupportedBrowser() &&
			bowser.isUnsupportedBrowser(
				{
					safari: '10',
				},
				globalThis.navigator.userAgent,
			)
		);
	}

	/**
	 * compareVersionNumbers compares two strings of dot separated integers such that we
	 * return 1 if a > b
	 * return -1 if a < b
	 * return 0 if a === b
	 * e.g. compareVersionNumbers(1.0, 1.0.1) returns -1,
	 * compareVersionNumbers(2.0, 1.0.3) returns 1,
	 * compareVersionNumbers(3.0, 3.0.0) returns 0
	 */
	compareVersionNumbers(a, b) {
		const aParts = a.split('.').map((str) => Number.parseInt(str, 10));
		const bParts = b.split('.').map((str) => Number.parseInt(str, 10));
		const maxParts = aParts.length > bParts.length ? aParts : bParts;

		for (let i = 0; i < maxParts.length; i++) {
			if (aParts[i] === undefined) {
				aParts[i] = 0;
			} else if (bParts[i] === undefined) {
				bParts[i] = 0;
			}

			if (aParts[i] > bParts[i]) {
				return 1;
			} else if (aParts[i] < bParts[i]) {
				return -1;
			}
		}

		return 0;
	}

	getUserId() {
		let ret = '';

		if (this.searchHostContext?.imsInfo.adobeId) {
			ret = this.searchHostContext.imsInfo.adobeId;
		}

		return ret;
	}

	getUserEmail() {
		let ret = '';

		if (this.searchHostContext?.imsInfo.email) {
			ret = this.searchHostContext.imsInfo.email;
		}

		return ret;
	}

	getLocale() {
		let ret = '';

		if (this.searchHostContext?.locale) {
			ret = this.searchHostContext.locale;
		}

		return ret;
	}

	getLocaleWithoutCountryCode() {
		let ret = this.searchHostContext?.locale;
		ret = ret ? langScript(ret) : '';
		return ret;
	}

	getAccessToken() {
		let ret = '';

		if (this.searchHostContext?.imsInfo.accessToken) {
			ret = this.searchHostContext.imsInfo.accessToken;
		}

		return ret;
	}

	isTouchDevice(): boolean {
		return (
			!!(
				typeof window !== 'undefined' &&
				('ontouchstart' in window ||
					(globalThis.DocumentTouch &&
						typeof document !== 'undefined' &&
						document instanceof globalThis.DocumentTouch))
			) || !!(typeof navigator !== 'undefined' && globalThis.navigator.maxTouchPoints)
		);
	}

	isChromeOS(): boolean {
		return navigator.userAgent.includes('CrOS');
	}

	isAndroid(): boolean {
		return platform?.os?.family?.toLowerCase() === 'android';
	}
	isIOS(): boolean {
		if (platform?.os?.family?.toLowerCase() === 'ios' && platform?.product?.toLowerCase() === 'iphone') {
			return true;
		}
		let width = screen.availWidth;
		if (screen.availHeight < screen.availWidth) {
			width = screen.availHeight;
		}
		return (
			this.isMacOS() &&
			this.isTouchDevice() &&
			width * devicePixelRatio < 1300 &&
			width * devicePixelRatio !== 768
		);
	}
	isIPadOS(): boolean {
		if (platform?.os?.family?.toLowerCase() === 'ios' && platform?.product?.toLowerCase() === 'ipad') {
			return true;
		}
		let width = screen.availWidth;
		if (screen.availHeight < screen.availWidth) {
			width = screen.availHeight;
		}
		return (
			this.isMacOS() &&
			this.isTouchDevice() &&
			(width * devicePixelRatio >= 1300 || width * devicePixelRatio === 768)
		);
	}

	getOSName(): string {
		if (this.isWin()) {
			return 'Windows';
		} else if (this.isChromeOS()) {
			return 'ChromeOS';
		} else if (this.isAndroid() || (this.isLinux() && this.isTouchDevice())) {
			return 'Android';
		} else if (this.isLinux() && !this.isTouchDevice()) {
			return 'Linux';
		} else if (this.isMacOS() && !this.isTouchDevice()) {
			return 'MacOS';
		} else if (this.isIOS()) {
			return 'IOS';
		} else if (this.isIPadOS()) {
			return 'IPadOS';
		}

		return globalThis.navigator.platform;
	}

	/**
	 * This method checks to see if the supplied type of storage is available for use. Taken from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
	 * @param {string} type the type of storage to check for.
	 */
	storageAvailable(type) {
		let storage;

		try {
			if (type === 'cookie') {
				let cookieEnabled = navigator.cookieEnabled;

				if (!cookieEnabled) {
					document.cookie = 'testcookie';
					cookieEnabled = document.cookie.includes('testcookie');
				}

				return cookieEnabled;
			}

			storage = window[type];
			const x = '__storage_test__';
			storage.setItem(x, x);
			storage.removeItem(x);
			return true;
		} catch (e) {
			/* istanbul ignore next */
			storage === undefined && getLogger().warn('Globals', 'Storage Unavailable', getErrorMessage(e));

			/*  ignore next */
			return (
				e instanceof DOMException && // everything except Firefox
				(e.code === 22 || // Firefox
					e.code === 1014 || // test name field too, because code might not be present
					// everything except Firefox
					e.name === 'QuotaExceededError' || // Firefox
					e.name === 'NS_ERROR_DOM_QUOTA_REACHED') && // acknowledge QuotaExceededError only if there's something already stored
				storage.length !== 0
			);
		}
	}

	/**
	 * Prevent Body to Scroll
	 */
	static disableBodyScroll() {
		// Only accept touchmove from fixed-element
		if (document.body) {
			// Bug https://jira.corp.adobe.com/browse/CCH-3515
			// touchmove events seems to not removable on Safari iOS 10 and below
			if (
				!Utils.isIOS &&
				!bowser.isUnsupportedBrowser(
					{
						safari: '10',
					},
					globalThis.navigator.userAgent,
				)
			) {
				/* istanbul ignore next */
				document.body.addEventListener(
					'touchmove',
					/* istanbul ignore else */
					(e) => e.preventDefault(),
				);
			}

			// $FlowFixMe
			document.body.classList.add('prevent-scrolling');
		}
	}

	/**
	 * Add scrolling back to Body
	 */
	static enableBodyScroll() {
		if (document.body) {
			document.body.removeEventListener(
				'touchmove',
				/* istanbul ignore else */
				(e) => e.preventDefault(),
			);
			// $FlowFixMe
			document.body.classList.remove('prevent-scrolling');
		}
	}

	/**
	 * This method can be used to provide animated vertical scrolling behavior on browsers that don't support scroll-behavior
	 * Based on https://stackoverflow.com/questions/17722497/scroll-smoothly-to-specific-element-on-page
	 * @param {*} scrollingElement the element to be scrolled
	 * @param {*} offset the distance the scrollingElement should be scrolled by
	 * @param {*} duration time in ms
	 */
	static scrollToY(scrollingElement, offset, duration) {
		let startingY = scrollingElement.scrollTop;

		if (scrollingElement === window) {
			// IE11 doesn't support scrollY so we use pageYOffset
			startingY = scrollingElement.pageYOffset;
		}

		let startTime = 0;
		globalThis.requestAnimationFrame(function step(currentTime) {
			if (!startTime) {
				startTime = currentTime;
			}

			// Time since start of animation
			const elapsedTime = currentTime - startTime;
			// Percentage of completion
			const percent = duration ? Math.min(elapsedTime / duration, 1) : 0;

			if (startingY === 0 && offset === 0) {
				globalThis.scrollTo(0, 0);
			}

			// Scroll the element to the next position
			// Element.ScrollTo is not supported on IE, Edge and Safari Mobile.
			if (Utils.isIE() || Utils.isEdge() || Utils.isSafari()) {
				scrollingElement.scrollTop = startingY + offset * percent;
			} else {
				scrollingElement.scrollTo(0, startingY + offset * percent);
			}

			// Continue animating for the duration specified
			if (elapsedTime < duration) {
				globalThis.requestAnimationFrame(step);
			}
		});
	}

	/**
	 * Returns the first vertically scrollable parent node for this node.
	 * @param {HTMLElement} node
	 */
	static getScrollingParent(node) {
		let parent: Window | HTMLElement;

		if (node) {
			const overflowY = node instanceof HTMLElement && globalThis.getComputedStyle(node).overflowY;
			const isScrollable = overflowY && !(overflowY.includes('visible') || overflowY.includes('hidden'));

			if (isScrollable && node.scrollHeight > node.clientHeight) {
				parent = node;
			} else {
				parent = Utils.getScrollingParent(node.parentNode) || window;
			}
			return parent;
		}

		return null;
	}

	static _currentBreakpoint = '';

	static onResize() {
		if (document.getElementById('search-container')) {
			const newBreakpoint = globalThis
				.getComputedStyle(document.getElementById('search-container') as Element, '::after')
				.getPropertyValue('content')
				.replaceAll(/"/g, '');

			/* istanbul ignore if */
			if (Utils._currentBreakpoint !== newBreakpoint) {
				Utils._currentBreakpoint = newBreakpoint;
				const event = new CustomEvent('breakpointChanged', {
					detail: {
						name: newBreakpoint,
					},
				});
				globalThis.dispatchEvent(event);
			}
		}
	}

	static get currentBreakpoint() {
		Utils.onResize();
		return Utils._currentBreakpoint;
	}

	static set currentBreakpoint(newBreakPoint) {
		Utils._currentBreakpoint = newBreakPoint;
	}
}

// Polyfill CustomEvent for IE11

/* istanbul ignore next */
(() => {
	// eslint-disable-line wrap-iife
	if (typeof globalThis.CustomEvent === 'function') {
		return false;
	}

	function CustomEvent(event, params) {
		const newParams = params || {
			bubbles: false,
			cancelable: false,
			detail: undefined,
		};
		const evt = document.createEvent('CustomEvent');
		evt.initCustomEvent(event, newParams.bubbles, newParams.cancelable, newParams.detail);
		return evt;
	}

	CustomEvent.prototype = globalThis.Event.prototype;
	// @ts-ignore
	globalThis.CustomEvent = CustomEvent;
})();

// For IE 10 and earlier

/* istanbul ignore if */
if (!globalThis.location.origin) {
	// @ts-ignore
	globalThis.location.origin = `${globalThis.location.protocol}//${globalThis.location.hostname}${
		globalThis.location.port ? `:${globalThis.location.port}` : ''
	}`; // eslint-disable-line prefer-template
}
